import { stateHelperModule } from '../../../../shared/app/helpers/state-helper/state-helper';
import * as EventUtils from '../../../../shared/app/utils/event-utils';
import { welcomeRouteModule } from './welcome/welcome.route';
import { challengesRouteModule } from './challenges/challenges.route';
import { secondsToTimeFilterModule } from '../../../../shared/app/filters/seconds-to-time';
import { reviewRouteModule } from './review/review.route';
import { assessmentResultProcessServiceModule } from './assessment-result-process';
import { assessmentsServiceModule } from '../../services/assessments-service/assessments-service';
import { assessmentResultsServiceModule } from '../../services/assessment-results-service/assessment-results-service';
import { photoManagerServiceModule } from '../../services/photo-manager-service/photo-manager-service';
import { accountServiceModule } from '../../../../shared/app/services/account-service/account-service';
import { uploadedImageComponentModule } from '../../../../shared/app/components/uploaded-image/uploaded-image';
import { timerWidgetComponentModule } from '../../components/timer-widget/timer-widget';
import assessmentsTemplate from './assessments.tpl.html';
import assessmentsNavTemplate from './assessments.nav.tpl.html';
import './assessments.less';
import { appConfig } from '../../../../shared/app/constants/app-config';
import { uiSrefNoLinkDirectiveModule } from '../../../../shared/app/components/ui-sref-no-link/ui-sref-no-link';
import { resetAssessmentComponentModule } from '../../components/reset-assessment-dialog/reset-assessment-dialog';
import { uiRouterStateModule } from '../../../../shared/app/vendor/ui-router-module';
import { productMaintenanceServiceModule } from '../../../../shared/app/services/product-maintenance-service/product-maintenance-service';
import { appAlertComponentModule } from '../../../../shared/app/components/app-alert/app-alert';
import { productMessageBannerComponentModule } from '../../../../shared/app/components/product-message-banner/product-message-banner';
import { messageHubServiceModule } from '../../services/message-hub-service/message-hub-service';
import { embedded } from '../../config/embedded';
import { dialogComponentModule } from '../../../../shared/app/components/dialog/dialog';
import { escapeHTML } from '../../../../shared/app/utils/string-utils';
import { deobfuscateId } from '../../../../embed/app/manager/id-obfuscation';
import {
  isProctoringSatisfied,
  shouldResumeProctoringCapture,
} from './proctoring-gate';

const DEFAULT_HEARTBEAT = 10;

export const assessmentsRouteModule = angular
  .module('Q.routes.assessments', [
    uiRouterStateModule,
    stateHelperModule,
    welcomeRouteModule,
    challengesRouteModule,
    secondsToTimeFilterModule,
    reviewRouteModule,
    assessmentResultProcessServiceModule,
    assessmentsServiceModule,
    assessmentResultsServiceModule,
    photoManagerServiceModule,
    accountServiceModule,
    uploadedImageComponentModule,
    timerWidgetComponentModule,
    uiSrefNoLinkDirectiveModule,
    resetAssessmentComponentModule,
    productMaintenanceServiceModule,
    appAlertComponentModule,
    productMessageBannerComponentModule,
    messageHubServiceModule,
    dialogComponentModule,
  ])

  .config(
    function ($stateProvider, $urlRouterProvider, $urlMatcherFactoryProvider) {
      let ObjectOrQId = $urlMatcherFactoryProvider.type('ObjectOrQId');
      $urlMatcherFactoryProvider.type('AssessmentId', {
        pattern: new RegExp(
          'practice(?:-\\w+)*|QE[-~\\w]{16}|' + ObjectOrQId.pattern.source,
        ),
      });

      // redirects for the old `welcome` routes
      $urlRouterProvider.when(
        '/{assessmentId:AssessmentId}/welcome',
        '/{assessmentId}',
      );
      $urlRouterProvider.when(
        '/{assessmentId:AssessmentId}/welcome/',
        '/{assessmentId}',
      );

      let assessmentTitle;

      $stateProvider.state('assessments', {
        abstract: true,
        url: '/{assessmentId:AssessmentId}?fromSetup&returnTo&redirectUrl&invite',
        params: {
          fromSetup: null,
          returnTo: null,
          redirectUrl: null,
          invite: null,
        },
        template: assessmentsTemplate,
        data: {
          bodyClass: 'assessments',
          pageTitle: function () {
            return assessmentTitle ? assessmentTitle : 'Assessment';
          },
        },
        resolve: {
          assessmentResultAuth: /* @ngInject */ function (
            $stateParams,
            invitationsService,
            messageHubService,
            $q,
            appAlert,
          ) {
            return messageHubService.whenReady().then(() => {
              const accessToken = $stateParams.invite;
              let assessmentId = $stateParams.assessmentId;
              if (/^QE/.test(assessmentId)) {
                if (embedded) {
                  assessmentId = deobfuscateId(assessmentId.slice(2));
                } else {
                  throw new Error('This URL only works while embedded');
                }
              }
              return invitationsService
                .authorize(accessToken, assessmentId)
                .then((auth) => {
                  if (auth.assessment) {
                    messageHubService.postLoaded({
                      assessment: auth.assessment,
                    });
                  }
                  return auth;
                })
                .catch((err) => {
                  if (err.data && err.data.reason) {
                    appAlert.error(err.data.reason);
                  }

                  return $q.reject(err);
                });
            });
          },
          assessment: /* @ngInject */ function (assessmentResultAuth) {
            return assessmentResultAuth.assessment;
          },
          maintenanceModeCheck: /* @ngInject */ function (
            assessmentResultAuth,
            productMaintenanceService,
            stateHelper,
          ) {
            return (
              !!assessmentResultAuth.candidate ||
              productMaintenanceService
                .checkLogin(assessmentResultAuth.assessment)
                .catch(() =>
                  stateHelper.redirectInResolve(
                    ...productMaintenanceService.errorState(),
                  ),
                )
            );
          },
          config: /* @ngInject */ function (configService) {
            return configService.load().catch(() => null);
          },
          process: /* @ngInject */ function (
            assessmentResultProcess,
            config,
            assessmentResultAuth,
          ) {
            let heartbeat =
              (config && config.challengeHeartbeat) || DEFAULT_HEARTBEAT;
            return assessmentResultProcess
              .create(assessmentResultAuth, heartbeat)
              .whenReady();
          },
          photoManager: /* @ngInject */ function (photoManagerService) {
            return photoManagerService.build();
          },
          proctoringSatisfied: /* @ngInject */ function (
            assessment,
            photoManager,
            process,
          ) {
            return () => {
              const { assessmentResult } = process;

              return isProctoringSatisfied({
                proctoringEnabled: assessment.proctoringEnabled(),
                proctoringDisabled: !!(
                  assessmentResult && assessmentResult.proctoringDisabled
                ),
                photoStored: photoManager.hasStoredPhoto(),
                resultHasStoredPhotos: !!(
                  assessmentResult && assessmentResult.hasProctoringImages
                ),
              });
            };
          },
        },
        controller: /* @ngInject */ function loginCtrl(
          $scope,
          assessment,
          assessmentResultAuth,
          process,
          accountService,
          $stateParams,
          $window,
          $state,
          resetAssessmentDialog,
          dialogService,
          photoManager,
          proctoringSatisfied,
          $log,
        ) {
          // clears the invite key when logging out
          if (assessment && assessment.inviteOnly) {
            appConfig.resetOnLogout = $state.href('loggedOut');
          } else {
            appConfig.resetOnLogout = $state.href('assessments', {
              invite: null,
            });
          }

          const ctrl = this;

          ctrl.navTemplate = assessmentsNavTemplate;

          let opener = null;
          try {
            if (
              $window.opener &&
              $window.opener.location.host === $window.location.host
            ) {
              opener = $window.opener;
            }
          } catch (ex) {}

          ctrl.hireUrl = appConfig.hireRoute;

          // if we've loaded an assessment, set up the reset return path for errors
          appConfig.onResetReturn = !!assessment && $state.href('.');

          ctrl.proctoringSatisfied = proctoringSatisfied;

          // Best effort: their stored photos already satisfy the gate, so failing here must not
          // lock them out of an assessment they are part-way through.
          const resumeProctoringCapture = () => {
            const { assessmentResult } = process;

            if (
              !assessmentResult ||
              !shouldResumeProctoringCapture({
                proctoringEnabled: assessment.proctoringEnabled(),
                proctoringDisabled: !!assessmentResult.proctoringDisabled,
                resultHasStoredPhotos: !!assessmentResult.hasProctoringImages,
                running: photoManager.running(),
              })
            ) {
              return;
            }

            photoManager
              .start({
                assessmentResultId: assessmentResult.id,
                teamCandidateId: assessmentResult.teamCandidateId,
              })
              // No welcome photo covers a resumed session.
              .then(() =>
                photoManager.takeAssessmentPhotos({ coversSessionStart: true }),
              )
              .catch((err) => {
                $log.error(
                  'Proctoring: could not resume capture on reload',
                  err,
                );
              });
          };
          process.whenAssessmentResultReady().then(resumeProctoringCapture);
          ctrl.account = accountService.get();
          ctrl.assessment = assessment;
          ctrl.isTeamMember = process.isTeamMember;
          ctrl.isPractice = process.isPractice;
          ctrl.isDemo = process.isDemo;
          ctrl.isSample = process.isSample;
          ctrl.hasCodeChallenges = process.hasCodeChallenges;
          ctrl.fromSetup = $stateParams.fromSetup;
          ctrl.canClose = () =>
            !!opener ||
            process.isTeamMember() ||
            (process.isSample() && $stateParams.returnTo);
          ctrl.restart = () => {
            if (process.isTeamMember()) {
              resetAssessmentDialog.open(process);
            } else {
              process.restart();
            }
          };
          ctrl.performPostAssessmentAction =
            process.performPostAssessmentAction;
          ctrl.close = ($event) => {
            let target = $event.target;
            let link = target && target.getAttribute('href');
            if (opener) {
              let linkPath = (target && target.pathname) || '';
              let windowPath = opener.location.pathname || '';
              let windowRoot = windowPath.match(/^\/\w+/);
              if (
                !link ||
                !windowRoot ||
                linkPath.indexOf(windowRoot[0]) === 0
              ) {
                EventUtils.stopEvent($event);
                if (link) {
                  opener.location = link;
                }
                if (opener.focus) {
                  opener.focus();
                }
                $window.close();
                return;
              }
              // we've got an opener, but it's to a different root app, so leave it be
            }

            if (!link && process.isTeamMember()) {
              EventUtils.stopEvent($event);
              // redirect to hire route
              $window.location = appConfig.hireRoute;
            }
          };

          ctrl.process = process;

          ctrl.showHelp = () => {
            let teamHelp;
            if (assessment.team && assessment.team.supportLink) {
              teamHelp = `<a href="${escapeHTML(assessment.team.supportLink)}">please click here to contact ${escapeHTML(assessment.team.name)}</a>`;
            } else {
              teamHelp = `please reach out to your contact at ${assessment.team ? escapeHTML(assessment.team.name) : 'the team that invited you.'}`;
            }
            dialogService.alert(
              `<p>
If you are having trouble with your ${escapeHTML(assessment.nounLower)},
<strong>${teamHelp}</strong>.
<br/>
They will be best able to help you work through issues with solving challenges.
</p>
<hr>
<p class="help-block">
You can also
<a href="${appConfig.docsUrl}/for-candidates" target="_blank" rel="noopener">use our documentation</a>
for help with using the Qualified application.
</p>`,
              'Getting Help',
            );
          };

          $scope.$on('$destroy', function () {
            ctrl.process.destroy();
            photoManager.stop();
          });
        },
        controllerAs: 'assessments',
      });
    },
  );
